/**
 * Singleton API client for Reviews module
 *
 * Centralizes all API calls with proper typing.
 * Uses Infrastructure's request helper.
 */

import { request } from '../../../Infrastructure'
import { getProductReviews, productReviewRatingsMetadata, createProductReview } from '../queries'

import type { Review, ReviewRating, CreateProductReviewRequest } from '../types'

// Exported response data types for hooks
export interface ProductReviewsData {
  products: {
    items: {
      reviews: {
        items: Review[]
      }
    }[]
  }
}

export interface ReviewRatingsData {
  productReviewRatingsMetadata: {
    items: ReviewRating[]
  }
}

export interface CreateReviewData {
  createProductReview: {
    review: Review
  }
}

/**
 * Reviews API client
 *
 * Note: request<T>() returns Promise<{ data: T }>, so we unwrap to return T directly
 */
export const reviewsApi = {
  /**
   * Fetch product reviews
   */
  getProductReviews: async (
    sku: string,
    pageSize: number,
    currentPage: number
  ): Promise<ProductReviewsData> => {
    const response = await request<ProductReviewsData>(getProductReviews, { sku, pageSize, currentPage })
    return response.data
  },

  /**
   * Fetch review ratings metadata
   */
  getRatingsMetadata: async (): Promise<ReviewRatingsData> => {
    const response = await request<ReviewRatingsData>(productReviewRatingsMetadata)
    return response.data
  },

  /**
   * Create a product review
   */
  createReview: async (input: CreateProductReviewRequest): Promise<CreateReviewData> => {
    const response = await request<CreateReviewData>(createProductReview, { input })
    return response.data
  },
}
